import React, { useEffect, useState } from 'react';
import {
  CalculatorIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ClockIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { api } from '../../services/authService';

const CalculatedFields = ({ contractData, selectedEquipment = [], onChange }) => {
  const [calculations, setCalculations] = useState({
    totalDays: 0,
    totalAmount: 0,
    equipmentAmount: 0,
    discountAmount: 0,
    netAmount: 0,
    depositAmount: 0,
    installmentValue: 0
  });

  const [editingDeposit, setEditingDeposit] = useState(false);
  const [tempDepositPercentage, setTempDepositPercentage] = useState('');
  const [tempDepositAmount, setTempDepositAmount] = useState('');

  // Estado para equipamentos do veículo
  const [vehicleEquipment, setVehicleEquipment] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);

  // Estado para configurações do contrato (incluindo caução padrão)
  const [contractSettings, setContractSettings] = useState(null);

  // Carregar configurações do contrato ao montar o componente
  useEffect(() => {
    const loadContractSettings = async () => {
      try {
        const response = await api.get('/api/contract-settings');
        setContractSettings(response.data.data);
        console.log('✅ Configurações carregadas:', response.data.data);

        // Se não houver deposit_percentage definido no contractData, usar o padrão das configurações
        if (onChange && !contractData.deposit_percentage && !contractData.deposit_amount && response.data.data?.deposit_percentage_default) {
          onChange({
            ...contractData,
            deposit_percentage: response.data.data.deposit_percentage_default
          });
        }
      } catch (error) {
        console.error('❌ Erro ao carregar configurações:', error);
      }
    };

    loadContractSettings();
  }, []); // Executa apenas uma vez ao montar

  useEffect(() => {
    recalculate();
  }, [
    contractData.start_date,
    contractData.end_date,
    contractData.monthly_rate,
    contractData.gross_amount,
    contractData.discount_percentage,
    contractData.discount_amount,
    contractData.deposit_percentage,
    contractData.number_of_installments,
    contractData.insurance_required,
    contractData.insurance_value,
    selectedEquipment,
    vehicleEquipment
  ]);

  // Carregar equipamentos do veículo automaticamente
  useEffect(() => {
    const loadVehicleEquipment = async () => {
      // Só carrega se tiver vehicle_id e datas válidas
      if (!contractData.vehicle_id || !contractData.start_date || !contractData.end_date) {
        setVehicleEquipment([]);
        return;
      }

      setLoadingEquipment(true);
      try {
        // 1. Carregar equipamentos atribuídos ao veículo
        const assignmentsResponse = await api.get(
          `/api/equipment-assignments?vehicle_id=${contractData.vehicle_id}&status=assigned`
        );
        const assignments = assignmentsResponse.data.data || [];

        if (assignments.length === 0) {
          setVehicleEquipment([]);
          setLoadingEquipment(false);
          return;
        }

        // 2. Carregar detalhes de todos os equipamentos disponíveis
        const equipmentResponse = await api.get('/api/auxiliary-equipment/available/list');
        const allEquipment = equipmentResponse.data.data || [];

        // 3. Filtrar e mapear equipamentos
        const assignedEquipmentIds = assignments.map(a => a.equipment_id);
        const equipmentWithDetails = allEquipment
          .filter(eq => assignedEquipmentIds.includes(eq.id))
          .filter(eq => parseFloat(eq.monthly_rate || 0) > 0) // Apenas com monthly_rate > 0
          .map(eq => {
            // Calcular meses de locação
            const startDate = new Date(contractData.start_date);
            const endDate = new Date(contractData.end_date);
            // Calcular meses usando diferença de calendário (end_date inclusivo)
            const adjEnd = new Date(endDate);
            adjEnd.setDate(adjEnd.getDate() + 1);
            const months = Math.max(
              (adjEnd.getFullYear() - startDate.getFullYear()) * 12 + (adjEnd.getMonth() - startDate.getMonth()),
              1
            );

            // Calcular valor total
            const monthlyRate = parseFloat(eq.monthly_rate || 0);
            const totalAmount = months * monthlyRate;

            return {
              equipment_id: eq.id,
              name: eq.name,
              description: eq.description,
              category: eq.category,
              monthly_rate: monthlyRate,
              quantity: 1,
              months: months,
              total_amount: totalAmount
            };
          });

        setVehicleEquipment(equipmentWithDetails);
        console.log(`✅ Carregados ${equipmentWithDetails.length} equipamento(s) do veículo`);
      } catch (error) {
        console.error('❌ Erro ao carregar equipamentos do veículo:', error);
        setVehicleEquipment([]);
      } finally {
        setLoadingEquipment(false);
      }
    };

    loadVehicleEquipment();
  }, [contractData.vehicle_id, contractData.start_date, contractData.end_date]);

  const recalculate = () => {
    const newCalcs = {};

    // Calcular total de dias e meses
    if (contractData.start_date && contractData.end_date) {
      const start = new Date(contractData.start_date);
      const end = new Date(contractData.end_date);
      const diffTime = Math.abs(end - start);
      const totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      newCalcs.totalDays = totalDays;
      // Calcular meses usando diferença de calendário (end_date é inclusivo)
      const adjustedEnd = new Date(end);
      adjustedEnd.setDate(adjustedEnd.getDate() + 1);
      newCalcs.totalMonths = Math.max(
        (adjustedEnd.getFullYear() - start.getFullYear()) * 12 + (adjustedEnd.getMonth() - start.getMonth()),
        1
      );
    }

    // Calcular valor total baseado em mensalidades
    if (contractData.monthly_rate && newCalcs.totalMonths) {
      newCalcs.totalAmount = contractData.monthly_rate * newCalcs.totalMonths;
      newCalcs.grossAmount = newCalcs.totalAmount;
    }

    // Calcular valor total dos equipamentos auxiliares (do veículo)
    newCalcs.equipmentAmount = 0;
    if (vehicleEquipment && vehicleEquipment.length > 0) {
      newCalcs.equipmentAmount = vehicleEquipment.reduce((sum, item) => sum + (item.total_amount || 0), 0);
    }

    // Adicionar equipamentos ao valor bruto
    if (newCalcs.grossAmount) {
      newCalcs.grossAmount = newCalcs.grossAmount + newCalcs.equipmentAmount;
    }

    // Adicionar seguro do veículo ao valor bruto
    newCalcs.insuranceAmount = 0;
    if (contractData.insurance_required && contractData.insurance_value > 0 && newCalcs.totalMonths) {
      newCalcs.insuranceAmount = contractData.insurance_value * newCalcs.totalMonths;
      if (newCalcs.grossAmount) {
        newCalcs.grossAmount = newCalcs.grossAmount + newCalcs.insuranceAmount;
      }
    }

    // Calcular desconto
    const gross = newCalcs.grossAmount || contractData.gross_amount || newCalcs.totalAmount || 0;
    if (contractData.discount_percentage) {
      newCalcs.discountAmount = (gross * contractData.discount_percentage) / 100;
    } else if (contractData.discount_amount) {
      newCalcs.discountAmount = contractData.discount_amount;
    }

    // Calcular valor líquido
    newCalcs.netAmount = gross - (newCalcs.discountAmount || 0);

    // Calcular caução
    // SEMPRE recalcular baseado no percentual se houver
    if (contractData.deposit_percentage && newCalcs.netAmount) {
      newCalcs.depositAmount = (newCalcs.netAmount * contractData.deposit_percentage) / 100;
    } else if (contractData.deposit_amount) {
      // Só usar valor fixo se NÃO houver percentual definido
      newCalcs.depositAmount = contractData.deposit_amount;
    }

    // Calcular parcelas
    if (contractData.number_of_installments && newCalcs.netAmount) {
      newCalcs.installmentValue = newCalcs.netAmount / contractData.number_of_installments;
    }

    setCalculations(newCalcs);

    // Notificar componente pai sobre mudanças
    if (onChange) {
      onChange({
        ...contractData,
        total_days: newCalcs.totalDays,
        total_amount: newCalcs.netAmount,
        gross_amount: newCalcs.grossAmount,
        discount_amount: newCalcs.discountAmount,
        net_amount: newCalcs.netAmount,
        deposit_amount: newCalcs.depositAmount,
        installment_value: newCalcs.installmentValue
      });
    }
  };

  const handleEditDeposit = () => {
    setTempDepositPercentage(contractData.deposit_percentage || '');
    setTempDepositAmount(contractData.deposit_amount || '');
    setEditingDeposit(true);
  };

  const handleDepositPercentageChange = (value) => {
    setTempDepositPercentage(value);
    // Recalcular valor fixo automaticamente baseado no percentual
    if (value && calculations.netAmount > 0) {
      const calculatedAmount = (calculations.netAmount * parseFloat(value)) / 100;
      setTempDepositAmount(calculatedAmount.toFixed(2));
    } else {
      setTempDepositAmount('');
    }
  };

  const handleDepositAmountChange = (value) => {
    setTempDepositAmount(value);
    // Recalcular percentual automaticamente baseado no valor fixo
    if (value && calculations.netAmount > 0) {
      const calculatedPercentage = (parseFloat(value) / calculations.netAmount) * 100;
      setTempDepositPercentage(calculatedPercentage.toFixed(2));
    } else {
      setTempDepositPercentage('');
    }
  };

  const handleSaveDeposit = () => {
    if (onChange) {
      const depositData = {
        ...contractData
      };

      // Determinar qual campo foi editado por último comparando com o valor atual
      const percentageChanged = tempDepositPercentage && parseFloat(tempDepositPercentage) !== contractData.deposit_percentage;
      const amountChanged = tempDepositAmount && parseFloat(tempDepositAmount) !== contractData.deposit_amount;

      if (amountChanged && tempDepositAmount && parseFloat(tempDepositAmount) > 0) {
        // Usuário definiu um VALOR FIXO - zerar percentual para usar valor fixo
        depositData.deposit_amount = parseFloat(tempDepositAmount);
        depositData.deposit_percentage = 0; // Zerar percentual!
      } else if (tempDepositPercentage && parseFloat(tempDepositPercentage) > 0) {
        // Usuário definiu um PERCENTUAL - zerar valor fixo para recalcular automaticamente
        depositData.deposit_percentage = parseFloat(tempDepositPercentage);
        depositData.deposit_amount = 0; // Zerar valor fixo!
      } else {
        // Nenhum valor - zerar ambos
        depositData.deposit_percentage = 0;
        depositData.deposit_amount = 0;
      }

      onChange(depositData);
    }
    setEditingDeposit(false);
  };

  const handleCancelDeposit = () => {
    setEditingDeposit(false);
    setTempDepositPercentage('');
    setTempDepositAmount('');
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value) => {
    if (!value) return '0';
    return value.toLocaleString('pt-BR');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <CalculatorIcon className="w-6 h-6 text-blue-500" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Cálculos Automáticos
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Total de Dias */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <CalendarIcon className="w-5 h-5 text-gray-500" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Total de Dias
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatNumber(calculations.totalDays)}
            <span className="text-sm font-normal text-gray-500 ml-2">dias</span>
          </p>
        </div>

        {/* Valor Bruto */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-2">
            <CurrencyDollarIcon className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-blue-700 dark:text-blue-400">
              Valor Bruto
            </span>
          </div>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">
            {formatCurrency(calculations.grossAmount || calculations.totalAmount)}
          </p>
          {calculations.totalMonths > 0 && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              {formatCurrency((calculations.grossAmount || calculations.totalAmount) / calculations.totalMonths)}/mês
            </p>
          )}
        </div>

        {/* Desconto */}
        {(contractData.discount_percentage > 0 || contractData.discount_amount > 0) && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-yellow-700 dark:text-yellow-400">
                Desconto
              </span>
              {contractData.discount_percentage > 0 && (
                <span className="text-xs font-medium px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded">
                  {contractData.discount_percentage}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">
              - {formatCurrency(calculations.discountAmount)}
            </p>
          </div>
        )}

        {/* Valor Líquido */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 mb-2">
            <CurrencyDollarIcon className="w-5 h-5 text-green-500" />
            <span className="text-sm text-green-700 dark:text-green-400 font-semibold">
              Valor Líquido
            </span>
          </div>
          <p className="text-2xl font-bold text-green-900 dark:text-green-200">
            {formatCurrency(calculations.netAmount)}
          </p>
          {calculations.totalMonths > 0 && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              {formatCurrency(calculations.netAmount / calculations.totalMonths)}/mês
            </p>
          )}
        </div>

        {/* Caução */}
        {(contractData.deposit_percentage > 0 || contractData.deposit_amount > 0 || editingDeposit) && (
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
            {!editingDeposit ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-purple-700 dark:text-purple-400">
                    Caução
                  </span>
                  <div className="flex items-center gap-2">
                    {contractData.deposit_amount > 0 ? (
                      <span className="text-xs font-medium px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded">
                        Valor Fixo
                      </span>
                    ) : (
                      <span className="text-xs font-medium px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded">
                        {contractData.deposit_percentage}%
                      </span>
                    )}
                    <button
                      onClick={handleEditDeposit}
                      className="p-1 text-purple-600 hover:bg-purple-100 dark:hover:bg-purple-900 rounded"
                      title="Editar Caução"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-200">
                  {formatCurrency(calculations.depositAmount)}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-purple-700 dark:text-purple-400">
                    Editar Caução
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveDeposit}
                      className="p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900 rounded"
                      title="Salvar"
                    >
                      <CheckIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleCancelDeposit}
                      className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded"
                      title="Cancelar"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-purple-700 dark:text-purple-400 mb-1">
                      Percentual (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={tempDepositPercentage}
                      onChange={(e) => handleDepositPercentageChange(e.target.value)}
                      className="w-full px-3 py-2 border border-purple-300 dark:border-purple-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Ex: 10"
                    />
                    {tempDepositPercentage && calculations.netAmount > 0 && (
                      <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                        = {formatCurrency((calculations.netAmount * parseFloat(tempDepositPercentage)) / 100)}
                      </p>
                    )}
                  </div>
                  <div className="text-center text-xs font-semibold text-purple-600 dark:text-purple-400">
                    OU
                  </div>
                  <div>
                    <label className="block text-xs text-purple-700 dark:text-purple-400 mb-1">
                      Valor Fixo (R$)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tempDepositAmount}
                      onChange={(e) => handleDepositAmountChange(e.target.value)}
                      className="w-full px-3 py-2 border border-purple-300 dark:border-purple-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Ex: 5000.00"
                    />
                    {tempDepositAmount && calculations.netAmount > 0 && (
                      <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                        = {((parseFloat(tempDepositAmount) / calculations.netAmount) * 100).toFixed(2)}% do valor líquido
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Seguro do Veículo */}
        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="w-5 h-5 text-orange-500" />
              <span className="text-sm text-orange-700 dark:text-orange-400 font-semibold">
                Seguro do Veículo
              </span>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={!!contractData.insurance_required}
              onChange={(e) => {
                if (onChange) {
                  onChange({
                    ...contractData,
                    insurance_required: e.target.checked,
                    insurance_value: e.target.checked ? (contractData.insurance_value || 0) : 0
                  });
                }
              }}
              className="w-4 h-4 text-orange-600 border-orange-300 rounded focus:ring-orange-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Incluir seguro do veículo na fatura mensal
            </span>
          </label>

          {contractData.insurance_required && (
            <div>
              <label className="block text-xs text-orange-700 dark:text-orange-400 mb-1">
                Valor Mensal do Seguro (R$)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={contractData.insurance_value || ''}
                onChange={(e) => {
                  if (onChange) {
                    onChange({
                      ...contractData,
                      insurance_value: parseFloat(e.target.value) || 0
                    });
                  }
                }}
                className="w-full px-3 py-2 border border-orange-300 dark:border-orange-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Ex: 500.00"
              />
              {contractData.insurance_value > 0 && calculations.totalMonths > 0 && (
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                  Total no contrato: {formatCurrency(contractData.insurance_value * calculations.totalMonths)} ({calculations.totalMonths} meses)
                </p>
              )}
            </div>
          )}
        </div>

        {/* Parcelas */}
        {contractData.number_of_installments > 1 && (
          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-indigo-700 dark:text-indigo-400">
                Valor da Parcela
              </span>
              <span className="text-xs font-medium px-2 py-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded">
                {contractData.number_of_installments}x
              </span>
            </div>
            <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-200">
              {formatCurrency(calculations.installmentValue)}
            </p>
          </div>
        )}
      </div>

      {/* Detalhes do Cálculo */}
      {calculations.totalMonths > 0 && contractData.monthly_rate > 0 && (
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
            Resumo do Cálculo
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                {formatNumber(calculations.totalMonths)} meses (aluguel veículo) x {formatCurrency(contractData.monthly_rate)}
              </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {formatCurrency(calculations.totalAmount)}
              </span>
            </div>

            {/* Equipamentos Auxiliares */}
            {vehicleEquipment && vehicleEquipment.length > 0 && (
              <>
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Equipamentos Auxiliares (vinculados ao veículo):
                  </div>
                  {vehicleEquipment.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm ml-2 mb-1">
                      <span className="text-gray-600 dark:text-gray-400">
                        {item.name} ({item.months} {item.months === 1 ? 'mês' : 'meses'} × {formatCurrency(item.monthly_rate)})
                      </span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {formatCurrency(item.total_amount)}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm ml-2 mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      Subtotal Equipamentos:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(calculations.equipmentAmount)}
                    </span>
                  </div>
                </div>
              </>
            )}

          {/* Seguro do Veículo no Resumo */}
          {contractData.insurance_required && contractData.insurance_value > 0 && (
            <div className="flex justify-between text-orange-700 dark:text-orange-400">
              <span>
                Seguro do Veículo ({formatNumber(calculations.totalMonths)} meses × {formatCurrency(contractData.insurance_value)})
              </span>
              <span className="font-medium">
                {formatCurrency(contractData.insurance_value * (calculations.totalMonths || 1))}
              </span>
            </div>
          )}

          {calculations.discountAmount > 0 && (
            <div className="flex justify-between text-yellow-700 dark:text-yellow-400">
              <span>
                Desconto {contractData.discount_percentage ? `(${contractData.discount_percentage}%)` : ''}
              </span>
              <span className="font-medium">
                - {formatCurrency(calculations.discountAmount)}
              </span>
            </div>
          )}

          <div className="flex justify-between pt-2 border-t border-gray-300 dark:border-gray-600 font-bold">
            <span className="text-gray-900 dark:text-white">Total</span>
            <span className="text-green-600 dark:text-green-400">
              {formatCurrency(calculations.netAmount)}
            </span>
          </div>

          {calculations.depositAmount > 0 && (
            <div className="flex justify-between text-purple-700 dark:text-purple-400 pt-2 border-t border-gray-300 dark:border-gray-600">
              <span>Caução ({contractData.deposit_percentage}%)</span>
              <span className="font-medium">
                {formatCurrency(calculations.depositAmount)}
              </span>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Aviso de Atualização Automática */}
      <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <ClockIcon className="w-4 h-4" />
        <span>Os valores são calculados automaticamente ao alterar datas ou valores</span>
      </div>
    </div>
  );
};

export default CalculatedFields;